// Apps list + edit drawer (ADMIN_UI §3.9 / §3.10). Five rows; row click opens
// the drawer with the AdjustTokens grid + every editable field. Save calls
// publishAppsInvalidate on the server so api/workers reload their cache.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AppEditForm } from '../../components/apps/AppEditForm.js';
import { AsyncState } from '../../components/AsyncState.js';
import { Badge } from '../../components/ui/badge.js';
import { Drawer } from '../../components/ui/drawer.js';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.js';
import { listApps } from '../../lib/api/apps.js';
import type { AppRecord } from '../../lib/api/types.js';
import { qk } from '../../lib/query-keys.js';

export const Route = createFileRoute('/_authed/apps')({
  component: AppsPage,
});

const statusVariant: Record<AppRecord['status'], 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

function AppsPage() {
  const [editing, setEditing] = useState<string | null>(null);
  const query = useQuery({ queryKey: qk.apps.all, queryFn: listApps });
  const editingApp =
    editing && query.data ? (query.data.apps.find((a) => a.code === editing) ?? null) : null;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold text-neutral-900">Apps</h2>
      <AsyncState
        query={query}
        isEmpty={(d) => d.apps.length === 0}
        empty={<p className="text-sm text-neutral-500">No apps configured.</p>}
      >
        {(data) => (
          <Table>
            <THead>
              <TR>
                <TH>Code</TH>
                <TH>Name</TH>
                <TH>Status</TH>
                <TH>Sending domain</TH>
                <TH>Subaccount</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {data.apps.map((app) => (
                <TR key={app.code} className="cursor-pointer" onClick={() => setEditing(app.code)}>
                  <TD className="font-mono text-xs uppercase">{app.code}</TD>
                  <TD>{app.name}</TD>
                  <TD>
                    <Badge variant={statusVariant[app.status]}>{app.status}</Badge>
                  </TD>
                  <TD className="font-mono text-xs">{app.sending_domain}</TD>
                  <TD>{app.sparkpost_subaccount_id ?? '—'}</TD>
                  <TD className="text-right text-xs text-neutral-500">Edit →</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </AsyncState>

      <Drawer
        open={!!editingApp}
        onClose={() => setEditing(null)}
        title={editingApp ? `Edit ${editingApp.name} (${editingApp.code})` : undefined}
      >
        {editingApp && <AppEditForm app={editingApp} onSaved={() => setEditing(null)} />}
      </Drawer>
    </div>
  );
}
